/**
 * User Editor
 * Create or edit workspace user
 */

import {
  Stack,
  Title,
  Input,
  Select,
  useToast,
  useActions,
  Icon,
  useTheme,
  H4
} from "@introist/react-foundation/v2";
import { DrawerHeader } from "../../foundation/templates/Drawer/DrawerHeader";
import { useState, useMemo } from "react";
import { useUser } from "../hooks/use-users";
import { usersApi, UserRole, CreateUserRequest, UpdateUserRequest } from "../api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { UnsavedChanges } from "../../components/molecules";
import { UserIntroistIdentity } from "../components/UserIntroistIdentity";
import { UserSlackIdentity } from "../components/UserSlackIdentity";

interface UserEditorProps {
  userId?: string;
  onClose: () => void;
}

export const UserEditor = ({ userId, onClose }: UserEditorProps) => {
  const { data: user, isLoading } = useUser(userId);
  const queryClient = useQueryClient();
  const toast = useToast();
  const { onConfirmAction } = useActions();
  const { theme } = useTheme();

  const isEditMode = !!userId;

  // Track only the changes for edit mode, or full form data for create mode
  const [changes, setChanges] = useState<Partial<CreateUserRequest | UpdateUserRequest>>(
    isEditMode ? {} : { role: "user" }
  );

  const hasChanges = useMemo(() => {
    return Object.keys(changes).length > 0;
  }, [changes]);

  const createMutation = useMutation({
    mutationFn: async () => {
      const data = changes as CreateUserRequest;
      if (!data.email || !data.role) {
        throw new Error("Email and role are required");
      }
      return usersApi.create(data);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["ai-studio", "users"] });
      setChanges({});
      toast.success("User created successfully");
      onClose();
    },
    onError: (error: any) => {
      toast.error(`Failed to create user: ${error.message}`);
    }
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error("User ID required");
      return usersApi.update(userId, changes);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["ai-studio", "users"] });
      setChanges({});
      toast.success("User updated successfully");
    },
    onError: (error: any) => {
      toast.error(`Failed to update user: ${error.message}`);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error("User ID required");
      return usersApi.delete(userId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["ai-studio", "users"] });
      toast.success("User deleted successfully");
      onClose();
    },
    onError: (error: any) => {
      toast.error(`Failed to delete user: ${error.message}`);
    }
  });

  const handleSave = async () => {
    if (isEditMode) {
      await updateMutation.mutateAsync();
    } else {
      await createMutation.mutateAsync();
    }
  };

  const handleDelete = onConfirmAction(
    async () => {
      await deleteMutation.mutateAsync();
    },
    {
      title: "Delete user",
      description: `Are you sure you want to delete ${user?.email}? This action cannot be undone.`,
      confirmTitle: "Delete"
    }
  );

  const handleDiscard = () => {
    setChanges({});
  };

  const roleOptions = [
    { key: "user", title: "User" },
    { key: "admin", title: "Admin" },
    { key: "owner", title: "Owner" }
  ];

  if (isEditMode && isLoading) {
    return (
      <Stack vertical style={{ padding: "var(--spacing-large)" }}>
        <DrawerHeader title="Loading..." onClose={onClose} />
      </Stack>
    );
  }

  if (isEditMode && !user) {
    return (
      <Stack vertical style={{ padding: "var(--spacing-large)" }}>
        <DrawerHeader title="User Not Found" onClose={onClose} />
        <Title>This user could not be found.</Title>
      </Stack>
    );
  }

  return (
    <Stack vertical style={{ padding: "var(--spacing-xLarge)", gap: "var(--spacing-large)" }}>
      <DrawerHeader
        title={isEditMode ? "Edit User" : "Create User"}
        onClose={onClose}
        actions={
          isEditMode
            ? [
                {
                  key: "delete",
                  title: "Delete",
                  onClick: handleDelete,
                  startAdornment: <Icon name="trash" color={theme.palette.danger.default} />,
                  titleStyles: { color: "var(--palette-danger-default)" }
                }
              ]
            : undefined
        }
      />

      <Stack vertical style={{ gap: "var(--spacing-medium)" }}>
        <Stack vertical style={{ gap: "var(--spacing-small)" }}>
          <Title variant="bold">Email *</Title>
          <Input
            size="small"
            value={changes.email ?? (user?.email || "")}
            onChange={value => setChanges(prev => ({ ...prev, email: value }))}
            placeholder="user@example.com"
            type="email"
          />
        </Stack>

        <Stack style={{ gap: "var(--spacing-small)" }}>
          <Stack vertical style={{ gap: "var(--spacing-small)", flex: 1 }}>
            <Title variant="bold">First Name</Title>
            <Input
              size="small"
              value={changes.firstname ?? (user?.firstname || "")}
              onChange={value => setChanges(prev => ({ ...prev, firstname: value }))}
            />
          </Stack>

          <Stack vertical style={{ gap: "var(--spacing-small)", flex: 1 }}>
            <Title variant="bold">Last Name</Title>
            <Input
              size="small"
              value={changes.lastname ?? (user?.lastname || "")}
              onChange={value => setChanges(prev => ({ ...prev, lastname: value }))}
            />
          </Stack>
        </Stack>

        <Stack vertical style={{ gap: "var(--spacing-small)" }}>
          <Title variant="bold">Role *</Title>
          <Select
            size="small"
            element="button"
            variant="outlined"
            value={changes.role ?? (user?.role || "user")}
            onSelect={opt => setChanges(prev => ({ ...prev, role: opt.key as UserRole }))}
            options={roleOptions}
            endAdornment={<Icon name="chevronDown" />}
          />
        </Stack>
      </Stack>

      {isEditMode && userId && (
        <Stack vertical style={{ marginTop: "var(--spacing-large)" }}>
          <H4>Linked Identities</H4>
          <UserIntroistIdentity userId={userId} />
          <UserSlackIdentity userId={userId} />
        </Stack>
      )}

      <UnsavedChanges
        show={hasChanges}
        onSave={handleSave}
        onDiscard={handleDiscard}
        style={{
          position: "sticky",
          bottom: "1rem",
          width: "320px",
          left: "calc(50% - 160px)"
        }}
      />
    </Stack>
  );
};
